/**
 * /payment/success — landing page after Stripe checkout.
 *
 * Polls /api/payments/status/{session_id} until payment_status === "paid"
 * (or ~24s elapse). Uses the webhook-fallback endpoint that also refreshes
 * from Stripe if the webhook hasn't landed yet.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(pence, currency = 'gbp') {
  if (pence == null) return '';
  const symbol = currency === 'gbp' ? '£' : currency.toUpperCase() + ' ';
  return `${symbol}${(pence / 100).toFixed(2)}`;
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    async function poll() {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === 'paid') return;   // done
        if (['failed', 'expired'].includes(data.payment_status)) return;
      } catch (e) {
        // Network hiccup — try again on next tick
      }
      if (cancelled) return;
      setAttempts((a) => {
        const next = a + 1;
        if (next > 12) { setTimedOut(true); return next; }  // ~24s (12 * 2s)
        setTimeout(poll, 2000);
        return next;
      });
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md" data-testid="payment-success-missing-session">
          <XCircle className="text-heritage" size={40}/>
          <h1 className="font-display text-2xl mt-3">Missing session</h1>
          <p className="text-charcoal/70 mt-2">We can't find your checkout session. If you were charged, please email <a className="text-heritage underline" href="mailto:info@goleicestershire.com">info@goleicestershire.com</a>.</p>
        </div>
      </div>
    );
  }

  const paid = status?.payment_status === 'paid';
  const failed = ['failed', 'expired'].includes(status?.payment_status);

  return (
    <div className="min-h-screen bg-stone flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center" data-testid="payment-success-card">
        {!status || (!paid && !failed && !timedOut) ? (
          <>
            <Loader2 className="animate-spin text-heritage mx-auto" size={40}/>
            <h1 className="font-display text-2xl mt-3">Confirming payment…</h1>
            <p className="text-charcoal/70 mt-2 text-sm">This usually takes a few seconds. Please don't close this window.</p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="text-forest mx-auto" size={48}/>
            <h1 className="font-display text-2xl mt-3" data-testid="payment-success-heading">Thank you — payment received</h1>
            <div className="text-charcoal/70 mt-3">
              <div>{status.quantity}× <b>{status.product_name}</b></div>
              <div className="mt-1 font-display text-lg text-charcoal">{money(status.amount_total, status.currency)}</div>
            </div>
            <p className="text-sm text-charcoal/60 mt-4">
              We'll email a confirmation shortly. If you have any questions email <a className="text-heritage underline" href="mailto:info@goleicestershire.com">info@goleicestershire.com</a>.
            </p>
            <Link to="/" data-testid="payment-success-home-link" className="inline-block mt-6 btn-primary px-6 py-2">Back to homepage</Link>
          </>
        ) : failed ? (
          <>
            <XCircle className="text-heritage mx-auto" size={40}/>
            <h1 className="font-display text-2xl mt-3">Payment didn't complete</h1>
            <p className="text-charcoal/70 mt-2">Your payment was not successful. No charge has been made.</p>
            <Link to="/partners/tourism-awards" className="inline-block mt-6 btn-primary px-6 py-2">Try again</Link>
          </>
        ) : (
          <>
            <Loader2 className="text-heritage mx-auto" size={40}/>
            <h1 className="font-display text-2xl mt-3">Still confirming…</h1>
            <p className="text-charcoal/70 mt-2 text-sm">Your payment is taking longer than usual. If you have any concerns, email <a className="text-heritage underline" href="mailto:info@goleicestershire.com">info@goleicestershire.com</a> and quote session {sessionId.slice(-8)}.</p>
          </>
        )}
      </div>
    </div>
  );
}
